// WS-8 P4 — Export "SERVICIOS PRESTADOS ROBOTPACK" (facturación por Q → Excel).
//
// Réplica del Excel que el cliente recibe: una hoja por ÁREA (Atlantas, Yogos,
// Multipack, Leches, ...) con el armado facturable + recargo dominical ×1.8, más
// una hoja CUADRO RESUMEN por área × Q. Todos los datos salen del response del
// GET /facturacion/q — el export es 100% client-side, sin endpoint nuevo.
//
// ⚠ ALCANCE: armado (una hoja por área) + TIENDAS (reempaque: unidades al
// precio del SKU + stickers/termos cobrados adicional, en el corte de su
// producción, v1 sin dominical). NO incluye servicios Emergentes sin OP
// programada ni Standby — la hoja "ALCANCE" lo deja escrito para que no se
// envíe creyendo que está completo.

import * as XLSX from 'xlsx';
import type { FacturacionQ, FacturacionQFila, FacturacionQTiendaFila } from 'schemas';
import { RECARGO_DOMINICAL } from 'schemas';

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** 'YYYY-MM-DD' → '23 may 2026' (vacío → ''). */
function fechaLegible(iso: string | null | undefined): string {
  if (!iso) return '';
  const [a, m, d] = iso.split('-');
  return `${Number(d)} ${MESES[Number(m) - 1]} ${a}`;
}

/** 'YYYY-MM-Q3' → 'Q3'. */
function corteQ(qKey: string): string {
  return qKey.split('-')[2] ?? qKey;
}

/** Excel: máx 31 chars, sin []:*?/\. Garantiza unicidad con sufijo. */
function nombreHojaSeguro(base: string, usados: Set<string>): string {
  let s =
    base
      .replace(/[[\]:*?/\\]/g, ' ')
      .trim()
      .slice(0, 31) || 'Hoja';
  let i = 2;
  while (usados.has(s.toLowerCase())) {
    const sufijo = ` (${i++})`;
    s = `${base.slice(0, 31 - sufijo.length)}${sufijo}`;
  }
  usados.add(s.toLowerCase());
  return s;
}

/**
 * Título del formato "Servicios prestados". Lleva el nombre del TENANT (la
 * empresa que PRESTA el servicio), nunca un nombre fijo: el archivo se envía
 * al cliente y cada empresa debe poder mandar el suyo. Sin nombre disponible
 * queda genérico — jamás se cae al nombre de otra empresa.
 */
export function tituloServiciosPrestados(
  tenantNombre?: string | null,
  detalle?: string | null,
): string {
  const empresa = (tenantNombre ?? '').trim();
  const base =
    empresa === '' ? 'SERVICIOS PRESTADOS' : `SERVICIOS PRESTADOS ${empresa.toUpperCase()}`;
  const sufijo = (detalle ?? '').trim();
  return sufijo === '' ? base : `${base} · ${sufijo}`;
}

/** Columnas del formato "SERVICIOS PRESTADOS" — armado (sin stickers). */
const HEADER = [
  'SEDE',
  'FECHA DE INICIO',
  'FECHA DE CONCILIACIÓN',
  'ORDEN DE PROCESO',
  'SKU',
  'DESCRIPCIÓN',
  'CANTIDAD PROGRAMADA DEL CLIENTE',
  'CANTIDAD SEMANAL',
  'CANTIDAD ARMADO DOMINICAL',
  'CANTIDAD ARMADA TOTAL',
  'TARIFA ARMADO SEMANAL',
  'TARIFA ARMADO DOMINICAL',
  'FACTURA ARMADO',
  'FACTURA ARMADO DOMINICAL',
  'TOTAL A FACTURAR',
  'CUMPLIMIENTO',
  'OBSERVACIÓN',
];

const anchos = [10, 14, 16, 15, 9, 40, 16, 13, 15, 15, 13, 14, 13, 15, 15, 13, 30];

function filaAoa(f: FacturacionQFila): (string | number)[] {
  const semanal = f.cantidad_validada - f.cantidad_validada_domingo;
  const precio = f.precio_unitario;
  const tarifaDom = f.dominical_aplica && precio != null ? precio * RECARGO_DOMINICAL : '';
  return [
    f.planta_nombre,
    fechaLegible(f.fecha_inicio),
    fechaLegible(f.fecha_facturacion),
    f.numero_op,
    f.sku_codigo_cliente,
    f.sku_descripcion,
    f.cantidad_programada,
    semanal,
    f.cantidad_validada_domingo,
    f.cantidad_validada,
    precio ?? 0,
    tarifaDom,
    f.valor_semanal,
    f.valor_dominical,
    f.valor_total,
    f.cumplimiento_pct != null ? f.cumplimiento_pct / 100 : '',
    f.sin_precio ? 'SIN TARIFA VIGENTE — cargar en Configuración → SKUs' : '',
  ];
}

/**
 * Genera y descarga el Excel del período cargado.
 *
 * @param clienteNombre  A quién se le factura (va en la hoja ALCANCE).
 * @param tenantNombre   Quién presta el servicio (encabeza cada hoja). Si no
 *                       llega, el título queda genérico.
 */
export function exportarFacturacionQ(
  data: FacturacionQ,
  clienteNombre: string,
  tenantNombre?: string | null,
): void {
  const rango = `${fechaLegible(data.desde)} – ${fechaLegible(data.hasta)}`;
  const wb = XLSX.utils.book_new();
  const usados = new Set<string>();

  // --- Hoja ALCANCE (primero, visible al abrir) ---
  const alcance: (string | number)[][] = [
    [tituloServiciosPrestados(tenantNombre)],
    [`Cliente: ${clienteNombre}`],
    [`Período: ${rango}`],
    [],
    ['⚠ ALCANCE DE ESTE ARCHIVO — LEER ANTES DE ENVIAR AL CLIENTE'],
    ['Incluye:'],
    ['• ARMADO por OP terminada (una hoja por área), solo producción VALIDADA por supervisor;'],
    ['  dominical ×1.8 solo si el cliente lo solicitó.'],
    ['• TIENDAS (reempaque): unidades al precio del SKU + stickers/termos cobrados adicional,'],
    ['  facturadas en el corte de su producción (v1 sin recargo dominical; dato del líder,'],
    ['  sin validación de supervisor).'],
    ['NO incluye:'],
    ['• Servicios Emergentes sin OP programada, ni Standby (se agregan a mano por ahora).'],
  ];
  const wsAlcance = XLSX.utils.aoa_to_sheet(alcance);
  wsAlcance['!cols'] = [{ wch: 95 }];
  XLSX.utils.book_append_sheet(wb, wsAlcance, nombreHojaSeguro('ALCANCE', usados));

  // --- Una hoja por área (orden de primera aparición) ---
  const areas: { codigo: string; nombre: string; filas: FacturacionQFila[] }[] = [];
  const idx = new Map<string, number>();
  for (const f of data.filas) {
    let i = idx.get(f.area_codigo);
    if (i === undefined) {
      i = areas.length;
      idx.set(f.area_codigo, i);
      areas.push({ codigo: f.area_codigo, nombre: f.area_nombre, filas: [] });
    }
    areas[i]!.filas.push(f);
  }

  for (const area of areas) {
    const aoa: (string | number)[][] = [
      [tituloServiciosPrestados(tenantNombre, `${area.nombre} · ${rango}`)],
      HEADER,
      ...area.filas.map(filaAoa),
    ];
    // Fila de total del área en las columnas de dinero (12=factura, 13=dom, 14=total).
    const totFactura = area.filas.reduce((s, f) => s + f.valor_semanal, 0);
    const totDom = area.filas.reduce((s, f) => s + f.valor_dominical, 0);
    const totTotal = area.filas.reduce((s, f) => s + f.valor_total, 0);
    const totalRow: (string | number)[] = new Array(HEADER.length).fill('');
    totalRow[3] = `TOTAL ${area.nombre}`;
    totalRow[12] = totFactura;
    totalRow[13] = totDom;
    totalRow[14] = totTotal;
    aoa.push([], totalRow);

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = anchos.map((wch) => ({ wch }));
    XLSX.utils.book_append_sheet(wb, ws, nombreHojaSeguro(area.nombre, usados));
  }

  // --- Hoja TIENDAS (reempaque: unidades + insumos cobrados) ---
  if (data.tiendas.filas.length > 0) {
    const HEADER_T = [
      'SEDE',
      'FECHA INICIO',
      'FECHA FIN',
      'ORDEN',
      'SKU',
      'DESCRIPCIÓN',
      'UNIDADES',
      'STICKERS',
      'TERMOS',
      'TARIFA UNIDAD',
      'TARIFA STICKER',
      'TARIFA TERMO',
      'VALOR UNIDADES',
      'INSUMOS COBRADOS',
      'TOTAL A FACTURAR',
      'OBSERVACIÓN',
    ];
    const filaTienda = (f: FacturacionQTiendaFila): (string | number)[] => [
      f.planta_nombre,
      fechaLegible(f.fecha_inicio),
      fechaLegible(f.fecha_fin),
      f.orden_produccion || '',
      f.sku_codigo_cliente,
      f.sku_descripcion,
      f.cantidad_unidades,
      f.cantidad_stickers,
      f.cantidad_termos,
      f.precio_unitario ?? 0,
      f.tarifa_sticker ?? '',
      f.tarifa_termo ?? '',
      f.valor_unidades,
      f.ingreso_insumos,
      f.valor_total,
      f.sin_precio ? 'SIN TARIFA VIGENTE — cargar en Configuración → SKUs' : '',
    ];
    const aoaT: (string | number)[][] = [
      [tituloServiciosPrestados(tenantNombre, `TIENDAS (reempaque) · ${rango}`)],
      HEADER_T,
      ...data.tiendas.filas.map(filaTienda),
    ];
    const totalRowT: (string | number)[] = new Array(HEADER_T.length).fill('');
    totalRowT[3] = 'TOTAL TIENDAS';
    totalRowT[12] = data.tiendas.total.valor_unidades;
    totalRowT[13] = data.tiendas.total.ingreso_insumos;
    totalRowT[14] = data.tiendas.total.valor_total;
    aoaT.push([], totalRowT);
    const wsT = XLSX.utils.aoa_to_sheet(aoaT);
    wsT['!cols'] = [10, 13, 13, 14, 9, 40, 11, 10, 9, 13, 13, 12, 14, 16, 16, 30].map((wch) => ({
      wch,
    }));
    XLSX.utils.book_append_sheet(wb, wsT, nombreHojaSeguro('TIENDAS', usados));
  }

  // --- Hoja CUADRO RESUMEN (por área × Q + tiendas) ---
  const tiendasPorQ = new Map<string, { unds: number; valor: number }>();
  for (const f of data.tiendas.filas) {
    const acc = tiendasPorQ.get(f.q_key) ?? { unds: 0, valor: 0 };
    acc.unds += f.cantidad_unidades;
    acc.valor += f.valor_total;
    tiendasPorQ.set(f.q_key, acc);
  }
  const qKeys = [...new Set([...data.qs.map((q) => q.q_key), ...tiendasPorQ.keys()])].sort();
  const resumen: (string | number)[][] = [['Corte', 'Área', 'OPs', 'Unds', 'Valor']];
  for (const qk of qKeys) {
    const q = data.qs.find((x) => x.q_key === qk);
    const filasQ = data.resumen_areas.filter((a) => a.q_key === qk);
    for (const a of filasQ) {
      resumen.push([corteQ(qk), a.area_nombre, a.ops, a.unidades_validadas, a.valor_total]);
    }
    const t = tiendasPorQ.get(qk);
    if (t) resumen.push([corteQ(qk), 'Tiendas', '', t.unds, t.valor]);
    resumen.push([
      corteQ(qk),
      'TOTAL',
      q?.ops ?? '',
      (q?.unidades_validadas ?? 0) + (t?.unds ?? 0),
      (q?.valor_total ?? 0) + (t?.valor ?? 0),
    ]);
  }
  const wsResumen = XLSX.utils.aoa_to_sheet(resumen);
  wsResumen['!cols'] = [{ wch: 8 }, { wch: 22 }, { wch: 8 }, { wch: 14 }, { wch: 16 }];
  XLSX.utils.book_append_sheet(wb, wsResumen, nombreHojaSeguro('CUADRO RESUMEN', usados));

  const nombreArchivo = `servicios-prestados-${clienteNombre}-${data.desde}_${data.hasta}.xlsx`
    .replace(/[^\w.\-]+/g, '-')
    .replace(/-+/g, '-');
  XLSX.writeFile(wb, nombreArchivo);
}
